import { Attributes, DateUtils, EnumHelper, Misc, Rules, Validation } from '@singularsystems/neo-core';
import TemporalEntity from '../TemporalEntity';
import { CompoundingType } from './CompoundingType.enum';

export default class Dividend extends TemporalEntity {
    static typeName = "Dividend";

    constructor() {
        super();
        this.makeObservable();
    }

    public dividendId: number = 0;

    @Attributes.Serialisation(Misc.SerialiseType.NotNull)
    public investmentId: number | null = 0;

    @Attributes.Date(Misc.TimeZoneFormat.None)
    public cycleDate: Date | null = null;

    @Rules.Required()
    @Attributes.Date(Misc.TimeZoneFormat.None)
    public dividendDate: Date | null = null;

    @Attributes.Date(Misc.TimeZoneFormat.None)
    public payDate: Date | null = null;

    @Attributes.Float()
    public dividendAmount: number = 0;

    @Attributes.Float()
    public dividendTax: number = 0;

    @Attributes.Float()
    public paidAmount: number = 0;

    @Attributes.Float()
    public reinvestAmount: number = 0;

    public compoundingType: CompoundingType | null = null;

    public ignore: boolean = false;

    // Client only properties / methods

    @Attributes.Float()
    public valuePerShare: number = 0;

    public get notes() {
        if (this.ignore) {
            return "Ignored";
        } else if (this.compoundingType !== null) {
            return "Compounding: " + EnumHelper.getItemMetadata(CompoundingType, this.compoundingType).display;
        } else {
            return null;
        }
    }

    public get dividendDateFooter() {
        if (this.cycleDate && this.outOfCycle(this.cycleDate)) {
            return `Dividend Cycle on ${DateUtils.format(this.cycleDate, "dd MMM yyyy")} will be ignored.`;
        }
        return "";
    }

    public shouldIgnore(calculationDate: Date) {
        return this.ignore || this.outOfCycle(calculationDate); 
    }

    public outOfCycle(calculationDate: Date) {
        return this.dividendDate !== null && this.dividendDate.getTime() !== calculationDate.getTime();
    }

    protected addBusinessRules(rules: Validation.Rules<this>) {
        super.addBusinessRules(rules);
        rules.failWhen(c => !c.payDate && !c.ignore, "Pay date is required");
        rules.failWhen(c => c.reinvestAmount > (c.dividendAmount - c.dividendTax), "Reinvest amount cannot exceed the available amount.").onProperties(c => c.reinvestAmount);
        rules.failWhen(
            c => c.reinvestAmount > 0 && c.valuePerShare > 0 && Math.abs((c.reinvestAmount / c.valuePerShare) - Math.round(c.reinvestAmount / c.valuePerShare)) > 0.00001,
            "Amount must be a whole multiple of the value per share."
        ).onProperties(c => c.reinvestAmount);
    }

    public toString(): string {
        if (this.isNew) {
            return "New dividend";
        } else {
            return "Dividend";
        }
    }
}